import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { CSSProperties } from 'react';
import { useEffect, useRef } from 'react';

import { CONTAINER, EYEBROW, NAMESPACE, Rich, SECTION } from '../components/ui';
import type { GeoData } from '../engine/geo';
import { initMulticonnection } from '../engine/multiconnection';
import { buildMcStrings, countryNamer } from '../strings';

// The resting state the design ships (São Paulo winning at 42 ms against the 208 ms direct line): the
// engine re-places the five rows for the visitor's location once the geo tables arrive.
const ROUTES = [
  { id: 'r1', code: 'MIA', city: 'Miami', cc: 'US', country: 'Estados Unidos', ms: 95, points: '1.5,1.5 8.5,5 15.5,2.3 22.5,1.5 29.5,4.1 36.5,5.9 43.5,2.3 50.5,3.2', dot: 3.2 },
  { id: 'r2', code: 'RIO', city: 'Rio de Janeiro', cc: 'BR', country: 'Brasil', ms: 52, points: '1.5,10.3 8.5,13 15.5,9.4 22.5,11.2 29.5,12.1 36.5,8.5 43.5,10.3 50.5,11.2', dot: 11.2 },
  { id: 'r3', code: 'SAO', city: 'São Paulo', cc: 'BR', country: 'Brasil', ms: 42, points: '1.5,11.9 8.5,12.8 15.5,11 22.5,12.8 29.5,11.9 36.5,13.7 43.5,12.8 50.5,13.7', dot: 13.7 },
  { id: 'r4', code: 'SCL', city: 'Santiago', cc: 'CL', country: 'Chile', ms: 77, points: '1.5,5.2 8.5,7.9 15.5,4.3 22.5,7 29.5,5.2 36.5,6.1 43.5,8.8 50.5,6.1', dot: 6.1 },
  { id: 'r5', code: 'BUE', city: 'Buenos Aires', cc: 'AR', country: 'Argentina', ms: 68, points: '1.5,8.8 8.5,6.1 15.5,9.7 22.5,7 29.5,7.9 36.5,9.7 43.5,6.1 50.5,7.9', dot: 7.9 },
];
const WINNER = 'r3';
const WINNER_MS = 42;
const BAD_MS = 208;

interface MulticonnectionProps {
  geo: GeoData | null;
  onCta: () => void;
}

export function Multiconnection({ geo, onCta }: MulticonnectionProps) {
  const { t } = useTranslation(NAMESPACE);
  const { locale = 'pt' } = useRouter();
  const rootRef = useRef<HTMLElement>(null);
  const countryName = countryNamer(locale);
  const strings = buildMcStrings(t, locale);

  useEffect(() => {
    if (!geo || !rootRef.current) return undefined;
    return initMulticonnection({ root: rootRef.current, geo, strings: buildMcStrings(t, locale), countryName: countryNamer(locale) });
  }, [geo, t, locale]);

  const winner = ROUTES.find((route) => route.id === WINNER)!;

  return (
    <section className={SECTION} id='multiconexao' aria-labelledby='mc-kicker'>
      <div className='pointer-events-none absolute inset-0 z-[-1] overflow-hidden' aria-hidden='true'>
        <span className='sec__grid' />
        <span className='sec__halo' />
      </div>
      <div className={CONTAINER}>
        <p className={EYEBROW} data-reveal>
          {t('mc.eyebrow')}
        </p>
        <h2 className='mt-4 max-w-[44em] break-words text-[clamp(1.125rem,.95rem+.8vw,1.5rem)] font-medium leading-[1.35] tracking-[-.015em] text-[#9AA3B5] [&_b]:font-bold [&_b]:text-white' id='mc-kicker' data-reveal>
          <Rich i18nKey='mc.kicker' />
        </h2>
      </div>

      <div className={`${CONTAINER} relative mt-[clamp(1.5rem,1rem+2vw,2.5rem)]`} id='mc-hold'>
        <div className='np-mc' data-np-comp='mc'>
          <section ref={rootRef} className='mc' aria-labelledby='mc-title' data-mc>
            <div className='mc-card'>
              <header className='mc-head'>
                <span className='eyebrow'>{t('mc.label')}</span>
                <h3 className='mc-title' id='mc-title'>
                  <span className='mc-title-a'>
                    {t('mc.title_a')} <span className='mc-title-b'>{t('mc.title_b')}</span>
                  </span>{' '}
                  <em>{t('mc.title_em')}</em>
                </h3>
                <p className='mc-lead'>
                  <Rich i18nKey='mc.lead' />
                </p>
                <ul className='mc-facts'>
                  <li>
                    <svg viewBox='0 0 24 24' aria-hidden='true'>
                      <path d='M14 3 5 13.5h6.5L10 21l9-10.5h-6.5z' />
                    </svg>
                    <span>
                      <Rich i18nKey='mc.fact_takeover' />
                    </span>
                  </li>
                  <li>
                    <svg viewBox='0 0 24 24' aria-hidden='true'>
                      <circle cx='12' cy='12' r='9' />
                      <path d='M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18' />
                    </svg>
                    <span>
                      <Rich i18nKey='mc.fact_servers' />
                    </span>
                  </li>
                </ul>
              </header>

              <div className='mc-proof' role='group' aria-label={t('mc.proof_aria')} data-proof>
                <p className='sr-only' data-proof-sr>
                  {strings.proofSr(BAD_MS, WINNER_MS, 80)}
                </p>
                <div className='mc-proof-rows' aria-hidden='true'>
                  <div className='mc-proof-item mc-proof-item--bad'>
                    <span className='mc-proof-line'>
                      <small>{t('mc.without')}</small>
                      <strong>
                        <span data-proof-bad>{BAD_MS}</span>
                        <span className='u'>ms</span>
                      </strong>
                    </span>
                    <span className='mc-proof-bar'>
                      <span className='mc-proof-fill' />
                    </span>
                  </div>
                  <div className='mc-proof-item mc-proof-item--good'>
                    <span className='mc-proof-line'>
                      <small>{t('mc.with')}</small>
                      <span className='mc-proof-pct'>
                        −<span data-proof-pct>80</span>%
                      </span>
                      <strong>
                        <span data-proof-good>{WINNER_MS}</span>
                        <span className='u'>ms</span>
                      </strong>
                    </span>
                    <span className='mc-proof-bar'>
                      <span className='mc-proof-fill' style={{ '--mc-fill': '20.2%' } as CSSProperties} data-proof-fill />
                    </span>
                  </div>
                </div>
                <p className='mc-proof-note'>
                  <Rich i18nKey='mc.proof_note' />
                </p>
              </div>

              <p className='sr-only' id='mc-desc' data-mc-desc>
                {strings.description(
                  ROUTES.map((route) => route.city),
                  winner.city
                )}
              </p>
              {/* the diagram is geometry (PC on the left, game server on the right), not reading order */}
              <div className='mc-stage' data-stage role='group' aria-label={t('mc.stage_aria')} aria-describedby='mc-desc' dir='ltr'>
                <svg className='mc-defs' aria-hidden='true' focusable='false'>
                  <defs>
                    <linearGradient id='mc-spg' x1='0' y1='0' x2='0' y2='1'>
                      <stop className='g0' offset='0' />
                      <stop className='g1' offset='1' />
                    </linearGradient>
                  </defs>
                </svg>
                <svg className='mc-wires' data-wires aria-hidden='true' focusable='false' />
                <div className='mc-packets' data-packets aria-hidden='true' />
                <div className='mc-node mc-node--pc' data-node='pc'>
                  <div className='mc-node-icon' aria-hidden='true'>
                    <svg viewBox='0 0 40 40'>
                      <rect x='5' y='7' width='30' height='20' rx='3' />
                      <path d='M14 34h12M20 27v7' />
                      <circle cx='20' cy='17' r='5' />
                      <path d='M20 9v3.5M20 21.5V25M12 17h3.5M24.5 17H28' />
                      <circle className='f' cx='20' cy='17' r='1.4' />
                    </svg>
                  </div>
                  <div className='mc-node-label'>
                    <strong>{t('mc.pc')}</strong>
                    <small data-pc-place>{t('mc.your_internet')}</small>
                  </div>
                </div>
                <div className='mc-routes-wrap'>
                  <div className='mc-key'>
                    <span className='mc-key-col' aria-hidden='true'>
                      <span className='x-long'>
                        <Rich i18nKey='mc.key_long' />
                      </span>
                      <span className='x-short'>
                        <Rich i18nKey='mc.key_short' />
                      </span>
                    </span>
                    <p className='mc-hint' data-hint aria-hidden='true'>
                      <svg className='x-touch' viewBox='0 0 24 24'>
                        <circle className='dot' cx='15' cy='15' r='2.4' />
                        <path d='M9.17 17.12A6.2 6.2 0 0 1 17.12 9.17M5.04 18.63A10.6 10.6 0 0 1 18.63 5.04' />
                      </svg>
                      <svg className='x-hover' viewBox='0 0 24 24'>
                        <path d='M5 3l14 8.2-6.4 1.6L9.4 19z' />
                      </svg>
                      <span className='x-touch'>{t('mc.hint_touch')}</span>
                      <span className='x-hover'>{t('mc.hint_hover')}</span>
                    </p>
                    <button className='mc-clear' type='button' data-clear hidden title={t('mc.clear_title')} aria-label={t('mc.clear_aria')}>
                      <svg viewBox='0 0 12 12' aria-hidden='true'>
                        <path d='M2.5 2.5l7 7M9.5 2.5l-7 7' />
                      </svg>
                      <span className='x-long'>{t('mc.clear_long')}</span>
                      <span className='x-short'>{t('mc.clear_short')}</span>
                    </button>
                  </div>
                  <ul className='mc-routes' data-routes aria-label={t('mc.routes_aria')}>
                    {ROUTES.map((route) => {
                      const isWinner = route.id === WINNER;
                      const country = countryName(route.cc, route.country);
                      return (
                        <li key={route.id}>
                          <button className={classNames('mc-route', isWinner && 'is-winner')} type='button' data-route={route.id} aria-pressed='false' aria-label={isWinner ? strings.routeAriaWinner(route.city, country, route.ms) : strings.routeAriaSlower(route.city, country, route.ms, route.ms - WINNER_MS)}>
                            <span className='mc-route-cc' aria-hidden='true'>
                              {route.code}
                            </span>
                            <span className='mc-route-body'>
                              <span className='mc-route-city'>{route.city}</span>
                              <span className='mc-route-meta'>
                                <span className='mc-route-country'>{country}</span>
                                <span className='mc-route-tag'>{t('mc.fastest')}</span>
                                <span className='mc-route-pin'>
                                  <svg viewBox='0 0 16 16' aria-hidden='true'>
                                    <path d='M9.6 1.8 14.2 6.4l-2 .5-1.8 1.8.4 3.4-1.3 1.3-2.9-2.9-4.2 4.2m3-5.5L2.6 6.4l1.3-1.3 3.4.4 1.8-1.8z' />
                                  </svg>
                                  {t('mc.pinned')}
                                </span>
                              </span>
                            </span>
                            <svg className='mc-route-spark' viewBox='0 0 52 18' aria-hidden='true' focusable='false'>
                              <polygon className='sp-fill' points={`1.5,18 ${route.points} 50.5,18`} />
                              <polyline points={route.points} />
                              <circle className='sp-dot' r='1.75' cx='50.5' cy={route.dot} />
                            </svg>
                            <span className='mc-route-lat'>
                              <span className='mc-route-ms'>
                                <span data-ms>{route.ms}</span>
                                <span className='u'>ms</span>
                              </span>
                              <span className='mc-route-delta' data-delta>
                                {isWinner ? strings.activeNow : strings.slowerBy(route.ms - WINNER_MS)}
                              </span>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <p className='mc-direct-key' aria-hidden='true'>
                    {t('mc.direct_key')}
                  </p>
                  <div className='mc-direct' data-direct role='group' aria-label={strings.directAria(BAD_MS - WINNER_MS)}>
                    <i aria-hidden='true' />
                    <span>
                      <b>{t('mc.direct_label')}</b>
                      <span className='x-d'>
                        <span className='x-sep' />
                        {t('mc.direct_sub')}
                      </span>
                    </span>
                    <strong>
                      +<span data-direct-ms>{BAD_MS - WINNER_MS}</span>
                      <span className='u'>ms</span>
                    </strong>
                  </div>
                </div>
                <div className='mc-node mc-node--game' data-node='game'>
                  <div className='mc-node-icon' aria-hidden='true'>
                    <svg viewBox='0 0 40 40'>
                      <rect x='7' y='7' width='26' height='10' rx='2.5' />
                      <rect x='7' y='23' width='26' height='10' rx='2.5' />
                      <path d='M12 12h6M12 28h6' />
                      <circle className='f' cx='27' cy='12' r='1.6' />
                      <circle className='f' cx='27' cy='28' r='1.6' />
                    </svg>
                  </div>
                  <div className='mc-node-label'>
                    <strong>{t('mc.game')}</strong>
                    <small data-game-place>{t('mc.game_default')}</small>
                  </div>
                </div>
                <div className='mc-result' aria-hidden='true' data-result>
                  <span className='mc-result-side mc-result-side--bad'>
                    <small>{t('mc.without')}</small>
                    <b>
                      <span data-result-bad>{BAD_MS}</span>
                      <i>ms</i>
                    </b>
                  </span>
                  <span className='mc-result-line'>
                    <svg viewBox='0 0 12 12'>
                      <path d='M3.5 1.5L8 6l-4.5 4.5' />
                    </svg>
                  </span>
                  <span className='mc-result-side mc-result-side--good'>
                    <small>{t('mc.with')}</small>
                    <span className='mc-result-fig'>
                      <span className='mc-proof-pct'>
                        −<span data-result-pct>80</span>%
                      </span>
                      <b>
                        <span data-result-good>{WINNER_MS}</span>
                        <i>ms</i>
                      </b>
                    </span>
                  </span>
                </div>
              </div>

              <footer className='mc-foot'>
                <div className='mc-foot-text'>
                  <p className='mc-status' data-status>
                    <i aria-hidden='true' />
                    {/* the engine rewrites this line (with live number spans) — React only paints the first one */}
                    {/* eslint-disable-next-line react/no-danger -- our own translation, markup limited to <b> */}
                    <span className='mc-status-body' data-status-text dangerouslySetInnerHTML={{ __html: t('mc.status_initial') }} />
                  </p>
                  <div className='mc-foot-slot'>
                    <p className='mc-bridge' data-bridge>
                      {t('mc.bridge')}
                    </p>
                  </div>
                </div>
                <div className='mc-actions'>
                  <button className='mc-pause' type='button' data-pause aria-label={t('mc.pause_aria')} aria-pressed='false' title={t('mc.pause_title')}>
                    <svg className='ic-pause' viewBox='0 0 24 24' aria-hidden='true'>
                      <path d='M8.5 5.5v13M15.5 5.5v13' />
                    </svg>
                    <svg className='ic-play' viewBox='0 0 24 24' aria-hidden='true'>
                      <path d='M8 5.2v13.6a.8.8 0 0 0 1.2.7l11-6.8a.8.8 0 0 0 0-1.4l-11-6.8a.8.8 0 0 0-1.2.7z' />
                    </svg>
                  </button>
                  <button className='btn btn-secondary mc-btn' type='button' data-retest aria-busy='false'>
                    <svg viewBox='0 0 24 24' aria-hidden='true'>
                      <path d='M20 11a8 8 0 1 0-2.3 5.7M20 4v7h-7' />
                    </svg>
                    <span className='mc-btn-lbl'>
                      <span data-lbl>{t('mc.retest')}</span>
                      <span className='mc-btn-ghost' aria-hidden='true'>
                        {t('mc.retest')}
                      </span>
                      <span className='mc-btn-ghost' aria-hidden='true'>
                        {t('mc.measuring')}
                      </span>
                    </span>
                  </button>
                  <div className='mc-offer'>
                    <a
                      className='btn btn-primary mc-cta'
                      data-cta
                      href='#np-form'
                      onClick={(event) => {
                        event.preventDefault();
                        onCta();
                      }}
                    >
                      <span>{t('common.cta')}</span>
                      <svg viewBox='0 0 24 24' aria-hidden='true'>
                        <path d='M5 12h13M13 6l6 6-6 6' />
                      </svg>
                    </a>
                    <p className='mc-offer-note'>
                      <Rich i18nKey='mc.offer_note' />
                    </p>
                  </div>
                </div>
                <p className='sr-only' role='status' aria-live='polite' data-live />
              </footer>
            </div>
          </section>
        </div>
      </div>
    </section>
  );
}
